import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Sparkle } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { fpPath } from "@/lib/site";
import FarmersNav from "@/components/FarmersNav";
import SEOHead from "@/components/SEOHead";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";

/**
 * FarmersCountryProfile — /guides/global-farming/:code
 *
 * Renders a country's farming profile: hero (flag + name + region), quick
 * facts, agricultural overview, top crops (linked back to /crops/<slug>),
 * seasons, extension bodies, references. Content grows one country at a
 * time via write_country_profile.py scripts (same pattern as crop articles).
 */
export default function FarmersCountryProfile() {
  const { code } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/countries/${(code || "").toUpperCase()}`)
      .then(({ data }) => setCountry(data.country || null))
      .catch(() => setCountry(null))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 pb-32 px-5"><div className="max-w-[1000px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }
  if (!country) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 pb-32 px-5" data-testid="country-notfound">
        <div className="max-w-[720px] mx-auto text-center">
          <h1 className="font-serif-display text-3xl">Country not found</h1>
          <Link to={fpPath("/guides/global-farming")} className="mt-4 inline-flex text-[color:var(--np-brand,#B85F2A)] underline">Back to Global Farming</Link>
        </div>
      </main>
    );
  }

  const listenText = `${country.name}. ${country.overview || "Country profile coming soon."} ${(country.top_crops || []).length ? "Top crops include " + (country.top_crops || []).slice(0, 5).map((c) => c.name || c).join(", ") + "." : ""}`;
  const status = (country.article_status || "not_started");

  return (
    <main className="bg-[#F5EBDC] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-country-profile">
      <SEOHead
        title={`${country.name} — Global Farming Guide · FarmersPulse`}
        description={country.overview || `Farming profile for ${country.name} — seasons, top crops, extension bodies and market channels.`}
        path={`/guides/global-farming/${country.code.toLowerCase()}`}
      />
      <FarmersNav />

      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/guides/global-farming")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]" data-testid="country-back">
          <ArrowLeft size={11} weight="bold" /> Global Farming
        </Link>

        {/* Hero */}
        <header className="mt-4 rounded-3xl border border-black/10 bg-white p-6 md:p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="text-6xl" data-testid="country-flag">{country.flag || "🏳️"}</div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)] inline-flex items-center gap-1">
                <MapPin size={11} weight="fill" /> {country.region} · {country.subregion}
              </div>
              <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight" data-testid="country-name">{country.name}</h1>
              {country.official_name && country.official_name !== country.name && (
                <div className="mt-1 text-sm italic text-[#3A3830]">{country.official_name}</div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <ListenTranslateBar text={listenText} testidPrefix="country-tools" />
                <ShareButton title={country.name} text={country.overview || country.name} variant="chip" testid="country-share" />
              </div>
            </div>
          </div>
        </header>

        {/* Article body — full profile once published */}
        {country.body_html ? (
          <article className="mt-6 rounded-2xl border border-black/10 bg-white p-6 md:p-8 prose prose-neutral max-w-none" data-testid="country-article">
            {country.hero_image_url && (
              <img src={country.hero_image_url} alt={country.hero_image_alt || country.name} className="not-prose w-full h-[320px] object-cover rounded-xl mb-6" loading="lazy" />
            )}
            <div dangerouslySetInnerHTML={{ __html: country.body_html }} />
            {country.hero_image_credit && (
              <p className="not-prose mt-4 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
                {country.hero_image_credit}
              </p>
            )}
          </article>
        ) : (
          <section className="mt-6 rounded-2xl border border-dashed border-black/15 bg-white/70 p-8 text-center" data-testid="country-empty">
            <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
            <div className="mt-2 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">Profile under research</div>
            <p className="mt-2 font-serif-display italic text-lg">
              The authoritative FarmersPulse guide for {country.name} is being researched. Come back soon — or explore a neighbouring country from the picker.
            </p>
            <Link to={fpPath("/guides/global-farming")} data-testid="country-empty-picker" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
              Pick another country
            </Link>
          </section>
        )}

        <p className="mt-6 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
          Status · {status.replace("_", " ")}
        </p>
      </div>
    </main>
  );
}
